import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { CheckCheck, Eye, Pencil, Search, SlidersHorizontal, Truck } from "lucide-react";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import { TableActionsMenu, type TableAction } from "@/components/shared/TableActionsMenu";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate } from "@/lib/dateFormat";
import { supplierKeys } from "@/lib/queryKeys";
import { listSuppliers, type Supplier } from "@/services/supplierService";

const SEARCH_DEBOUNCE_MS = 300;

export function SuppliersPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pagination = usePaginationFromUrl();

  const filterSearch = searchParams.get("search") ?? "";
  const filterDocumentType = searchParams.get("documentType") ?? "";
  const filterAutoAck = searchParams.get("autoAck") ?? "";

  const [searchInput, setSearchInput] = useState(filterSearch);
  useEffect(() => {
    setSearchInput(filterSearch);
  }, [filterSearch]);

  const setFilter = useCallback(
    (key: "search" | "documentType" | "autoAck", value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          next.set("page", "1");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("search");
        next.delete("documentType");
        next.delete("autoAck");
        next.set("page", "1");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const hasActiveFilters = useMemo(
    () => Boolean(filterSearch.trim() || filterDocumentType || filterAutoAck),
    [filterSearch, filterDocumentType, filterAutoAck],
  );

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filterDocumentType) n++;
    if (filterAutoAck) n++;
    return n;
  }, [filterDocumentType, filterAutoAck]);

  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== filterSearch) {
        setFilter("search", trimmed);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchInput, filterSearch, setFilter]);

  const listParams = useMemo(
    () => ({
      page: pagination.page,
      limit: pagination.limit,
      ...(filterSearch.trim() && { search: filterSearch.trim() }),
      ...(filterDocumentType && {
        documentType: filterDocumentType as "RUT" | "CI" | "EXT",
      }),
      ...(filterAutoAck === "yes" && { autoAckCfeOnReceive: true }),
      ...(filterAutoAck === "no" && { autoAckCfeOnReceive: false }),
    }),
    [
      pagination.page,
      pagination.limit,
      filterSearch,
      filterDocumentType,
      filterAutoAck,
    ],
  );

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: supplierKeys.list(listParams),
    queryFn: () => listSuppliers(listParams),
  });

  useEffect(() => {
    if (data != null) {
      pagination.setTotal(data.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [data, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(
        t(
          "suppliers.list.error",
          "Unable to load suppliers. The API may not be available yet.",
        ),
      );
    }
  }, [isError, t]);

  const suppliers: Supplier[] = data?.data ?? [];

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        eyebrow={t("suppliers.list.eyebrow", "Tu cartera de proveedores")}
        title={t("suppliers.list.title", "Proveedores")}
        actions={
          <Button asChild>
            <Link to="/suppliers/new">
              {t("suppliers.list.addSupplier", "Agregar proveedor")}
            </Link>
          </Button>
        }
      />
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1 sm:max-w-[260px] sm:flex-none">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            placeholder={t(
              "suppliers.list.filters.searchPlaceholder",
              "Buscar por nombre o RUT",
            )}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-9"
            aria-label={t(
              "suppliers.list.filters.searchPlaceholder",
              "Buscar por nombre o RUT",
            )}
          />
        </div>

        <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
          <DialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 gap-2 transition-colors duration-150"
            >
              <SlidersHorizontal className="h-4 w-4 shrink-0" aria-hidden />
              <span className="truncate">
                {t("suppliers.list.filters.filters", "Filtros")}
              </span>
              {activeFilterCount > 0 ? (
                <Badge variant="secondary" className="shrink-0 tabular-nums">
                  {activeFilterCount}
                </Badge>
              ) : null}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[min(90vh,720px)] w-[calc(100vw-1.5rem)] max-w-lg gap-0 overflow-hidden p-0 sm:rounded-xl">
            <DialogHeader className="border-b border-border px-4 py-4 text-left">
              <DialogTitle className="text-foreground">
                {t(
                  "suppliers.list.filters.filtersSheetTitle",
                  "Filtrar proveedores",
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="flex max-h-[min(70vh,560px)] flex-col gap-5 overflow-y-auto overscroll-contain px-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="suppliers-filter-document-type">
                  {t("suppliers.list.filters.documentType", "Tipo de documento")}
                </Label>
                <Select
                  value={filterDocumentType || "all"}
                  onValueChange={(v) =>
                    setFilter("documentType", v === "all" ? "" : v)
                  }
                >
                  <SelectTrigger
                    id="suppliers-filter-document-type"
                    className="w-full"
                  >
                    <SelectValue
                      placeholder={t(
                        "suppliers.list.filters.allDocumentTypes",
                        "Todos los tipos",
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t(
                        "suppliers.list.filters.allDocumentTypes",
                        "Todos los tipos",
                      )}
                    </SelectItem>
                    <SelectItem value="RUT">RUT</SelectItem>
                    <SelectItem value="CI">CI</SelectItem>
                    <SelectItem value="EXT">EXT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="suppliers-filter-auto-ack">
                  {t(
                    "suppliers.list.filters.autoAck",
                    "Aceptación automática",
                  )}
                </Label>
                <Select
                  value={filterAutoAck || "all"}
                  onValueChange={(v) =>
                    setFilter("autoAck", v === "all" ? "" : v)
                  }
                >
                  <SelectTrigger
                    id="suppliers-filter-auto-ack"
                    className="w-full"
                  >
                    <SelectValue
                      placeholder={t(
                        "suppliers.list.filters.autoAckAll",
                        "Todos",
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("suppliers.list.filters.autoAckAll", "Todos")}
                    </SelectItem>
                    <SelectItem value="yes">
                      {t("suppliers.list.filters.autoAckYes", "Activada")}
                    </SelectItem>
                    <SelectItem value="no">
                      {t("suppliers.list.filters.autoAckNo", "Desactivada")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div
              className={cn(
                "flex flex-col gap-2 border-t border-border bg-muted/30 px-4 py-3 sm:flex-row sm:items-center",
                activeFilterCount > 0 ? "sm:justify-between" : "sm:justify-end",
              )}
            >
              {activeFilterCount > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full transition-colors duration-150 sm:w-auto"
                  onClick={clearFilters}
                >
                  {t("suppliers.list.filters.clearFilters", "Limpiar filtros")}
                </Button>
              ) : null}
              <Button
                type="button"
                className="w-full transition-colors duration-150 sm:w-auto"
                onClick={() => setFiltersOpen(false)}
              >
                {t("suppliers.list.filters.filtersDone", "Listo")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {hasActiveFilters ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground transition-colors duration-150"
            onClick={clearFilters}
          >
            {t("suppliers.list.filters.clearFilters", "Limpiar filtros")}
          </Button>
        ) : null}
      </div>
      <TableCard>
        {isLoading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : suppliers.length === 0 ? (
          <EmptyState
            icon={<Truck />}
            title={t("suppliers.list.empty", "No hay proveedores todavía.")}
            message={t(
              "suppliers.list.emptyHint",
              "Los proveedores se crean automáticamente cuando recibís un EnvioCFE de un emisor nuevo, o podés agregarlos manualmente.",
            )}
            action={
              <Button asChild>
                <Link to="/suppliers/new">
                  {t("suppliers.list.addSupplier", "Agregar proveedor")}
                </Link>
              </Button>
            }
          />
        ) : (
          <>
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t("suppliers.list.legalName", "Razón social")}
                    </TableHead>
                    <TableHead>
                      {t("suppliers.list.taxId", "RUT")}
                    </TableHead>
                    <TableHead>
                      {t("suppliers.list.autoAck", "Auto-ACK")}
                    </TableHead>
                    <TableHead>
                      {t("suppliers.list.dateAdded", "Agregado")}
                    </TableHead>
                    <TableHead className="w-[60px]" aria-hidden />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suppliers.map((s) => {
                    const actions: TableAction[] = [
                      {
                        label: t("suppliers.list.edit", "Editar"),
                        icon: Pencil,
                        onClick: () => navigate(`/suppliers/${s.id}/edit`),
                      },
                      {
                        label: t("suppliers.list.viewDetails", "Ver detalles"),
                        icon: Eye,
                        onClick: () => navigate(`/suppliers/${s.id}`),
                      },
                    ];
                    const dateAdded = s.createdAt
                      ? formatDisplayDate(s.createdAt, {
                          locale: i18n.language,
                        })
                      : "—";
                    return (
                      <TableRow
                        key={s.id}
                        onRowClick={() => navigate(`/suppliers/${s.id}`)}
                      >
                        <TableCell className="font-medium">
                          <div className="flex flex-col">
                            <span>{s.legalName}</span>
                            {s.displayName?.trim() ? (
                              <span className="text-xs text-muted-foreground">
                                {s.displayName}
                              </span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {s.documentType === "EXT"
                            ? (s.docRecepExt?.trim() ?? "—")
                            : (s.rut ?? "—")}
                        </TableCell>
                        <TableCell>
                          {s.autoAckCfeOnReceive ? (
                            <StatusBadge
                              variant="success"
                              className="inline-flex items-center gap-1"
                            >
                              <CheckCheck className="h-3 w-3" aria-hidden />
                              <span>
                                {t("suppliers.list.autoAckOn", "Activada")}
                              </span>
                            </StatusBadge>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              {t("suppliers.list.autoAckOff", "Manual")}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {dateAdded}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <TableActionsMenu actions={actions} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="space-y-3 py-2 md:hidden">
              {suppliers.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => navigate(`/suppliers/${s.id}`)}
                  className="w-full rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-border/80"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">
                        {s.legalName}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground tabular-nums">
                        {s.documentType === "EXT"
                          ? (s.docRecepExt?.trim() ?? "—")
                          : (s.rut ?? "—")}
                      </div>
                    </div>
                    {s.autoAckCfeOnReceive ? (
                      <StatusBadge
                        variant="success"
                        className="inline-flex shrink-0 items-center gap-1"
                      >
                        <CheckCheck className="h-3 w-3" aria-hidden />
                        <span>
                          {t("suppliers.list.autoAckOn", "Activada")}
                        </span>
                      </StatusBadge>
                    ) : null}
                  </div>
                </button>
              ))}
            </div>
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t(
                "suppliers.list.rowsPerPage",
                "Filas por página",
              )}
              pageOfLabel={t(
                "suppliers.list.pageOf",
                "{{from}}-{{to}} de {{total}}",
                {
                  from: (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  ),
                  total: pagination.total,
                },
              )}
              onRefresh={() => void refetch()}
              isRefreshing={isFetching}
            />
          </>
        )}
      </TableCard>
    </div>
  );
}
